import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Instagram, Youtube, Music2, Mail, Lock } from "lucide-react";
import { TID } from "@/constants/testIds";
import { postNewsletter } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setBusy(true);
      await postNewsletter(email);
      toast.success("You're on the list. Studio notes from Detroit.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="mt-24 border-t border-[hsl(var(--line))] bg-[hsl(var(--bg-secondary))] relative z-10">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <p className="catalog-label mb-4">Studio</p>
          <h3 className="font-serif text-3xl leading-tight mb-4">
            Colin Darke
            <br />
            <span className="text-[hsl(var(--ink-muted))]">
              The Ambidextrous Artist
            </span>
          </h3>
          <p className="max-w-md text-[hsl(var(--ink-muted))]">
            Detroit-based American artist. Four decades of drawing and
            painting — often with both hands at once — challenging himself
            to make the process itself part of the piece.
          </p>

          <form
            onSubmit={onSubmit}
            className="mt-6 flex items-center gap-0 max-w-md"
          >
            <input
              type="email"
              required
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={TID.newsletterInput}
              className="flex-1 h-11 px-4 bg-[hsl(var(--surface))] border border-[hsl(var(--line))] focus:border-[hsl(var(--ink))] outline-none text-sm"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid={TID.newsletterSubmit}
              className="h-11 px-5 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] text-sm tracking-widest uppercase font-mono hover:bg-[hsl(var(--vermillion))] transition-colors"
            >
              {busy ? "…" : "Subscribe"}
            </button>
          </form>
        </div>

        <div>
          <p className="catalog-label mb-4">Explore</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-[hsl(var(--vermillion))]">About Colin</Link></li>
            <li><Link to="/techniques" className="hover:text-[hsl(var(--vermillion))]">How He Does It</Link></li>
            <li><Link to="/artwork" className="hover:text-[hsl(var(--vermillion))]">Artwork</Link></li>
            <li><Link to="/press" className="hover:text-[hsl(var(--vermillion))]">Press &amp; Media</Link></li>
            <li><Link to="/commission" className="hover:text-[hsl(var(--vermillion))]">Commission a Portrait</Link></li>
            <li><Link to="/contact" className="hover:text-[hsl(var(--vermillion))]">Contact</Link></li>
          </ul>
        </div>

        <div>
          <p className="catalog-label mb-4">Follow</p>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                href="https://www.instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-[hsl(var(--vermillion))]"
                data-testid="social-instagram"
              >
                <Instagram size={16} /> Instagram
              </a>
            </li>
            <li>
              <a
                href="https://www.tiktok.com/@colindarke"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-[hsl(var(--vermillion))]"
                data-testid="social-tiktok"
              >
                <Music2 size={16} /> TikTok
              </a>
            </li>
            <li>
              <a
                href="https://www.youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-[hsl(var(--vermillion))]"
                data-testid="social-youtube"
              >
                <Youtube size={16} /> YouTube
              </a>
            </li>
            <li>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 hover:text-[hsl(var(--vermillion))]"
              >
                <Mail size={16} /> Press &amp; Studio
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs font-mono tracking-widest uppercase text-[hsl(var(--ink-muted))]">
          <span>© {new Date().getFullYear()} Colin Darke Studio · Detroit, Michigan</span>
          <span className="hidden md:inline">Making the work in front of you. For four decades.</span>
          <Link
            to={user ? "/admin" : "/admin/login"}
            data-testid="footer-studio-login"
            className="inline-flex items-center gap-2 hover:text-[hsl(var(--vermillion))] transition-colors"
          >
            <Lock size={12} /> {user ? "Studio admin" : "Studio login"}
          </Link>
        </div>
      </div>
    </footer>
  );
}
